// Phase 8c — public /demos gallery.
//
// Renders a grid of pre-baked kinematic intercept demonstrations harvested
// from the 6 ``is_system_demo: true`` preset scenarios. NO auth, NO live
// simulation, NO disclaimer — per human direction. Trajectory data is
// served statically from /api/public/demos/<slug> and rendered with the
// existing Trajectory3D viewer (passed a custom fetcher so it hits the
// public endpoint instead of the authed /api/runs/{id}/trajectory-data).
//
// Two views in this file:
//   - PublicDemosListPage  → grid of 6 cards at /demos
//   - PublicDemoViewerPage → single demo + viewer at /demos/:slug
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Play } from "lucide-react";

import EmptyState from "@/components/EmptyState";
import Skeleton from "@/components/Skeleton";
import Trajectory3D from "@/components/Trajectory3D";
import { fmtNum } from "@/lib/format";

const PAGE_TITLE = "Live simulation demonstrations";
const PAGE_LEAD =
    "Six pre-recorded intercept engagements drawn from the system " +
    "demonstration scenario set, rendered in the same trajectory viewer " +
    "the operator console uses. Click any demonstration to step through " +
    "its trajectory frame-by-frame.";

const API_BASE = "";  // same-origin: page at /demos hits /api/public/demos directly

async function _fetchJson(path) {
    // Public endpoints — explicit fetch, no auth header, no axios
    // interceptors. Always same-origin (the page is served from the
    // same host as the API behind the kubernetes ingress).
    const r = await fetch(`${API_BASE}${path}`, {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "omit",
    });
    if (!r.ok) throw new Error(`HTTP ${r.status} on ${path}`);
    return r.json();
}


// --------------------------------------------------------------------
// PublicDemosListPage — grid of 6 demos
// --------------------------------------------------------------------
export function PublicDemosListPage() {
    const [demos, setDemos] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        _fetchJson("/api/public/demos")
            .then((d) => {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.log("[PublicDemosListPage] fetched", d);
                setDemos((d && d.demos) || []);
            })
            .catch((e) => {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error("[PublicDemosListPage] fetch failed", e);
                setError(e?.message || "Failed to load demos.");
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <div
            className="public-demos-page"
            data-testid="public-demos-page"
            style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 16px" }}
        >
            <header style={{ marginBottom: 24 }}>
                <h1
                    style={{ fontSize: 28, fontWeight: 600, marginBottom: 12 }}
                    data-testid="public-demos-title"
                >
                    {PAGE_TITLE}
                </h1>
                <p
                    style={{
                        color: "var(--ux-text-muted, #a7b1c2)",
                        maxWidth: 760,
                        lineHeight: 1.5,
                    }}
                    data-testid="public-demos-lead"
                >
                    {PAGE_LEAD}
                </p>
            </header>

            {error && (
                <EmptyState
                    title="Could not load demonstrations"
                    description={String(error)}
                    data-testid="public-demos-error"
                />
            )}

            {!error && demos === null && (
                <div data-testid="public-demos-loading">
                    <Skeleton style={{ height: 120, marginBottom: 12 }} />
                    <Skeleton style={{ height: 120, marginBottom: 12 }} />
                    <Skeleton style={{ height: 120 }} />
                    <pre style={{ color: "yellow", fontSize: 11 }} data-testid="public-demos-debug">
                        DEBUG: demos=null (loading)
                    </pre>
                </div>
            )}

            {!error && demos !== null && demos.length === 0 && (
                <EmptyState
                    title="No demonstrations available"
                    description="No pre-baked demonstrations are currently published."
                    data-testid="public-demos-empty"
                />
            )}

            {!error && demos !== null && demos.length > 0 && (
                <div
                    className="public-demos-grid"
                    data-testid="public-demos-grid"
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                        gap: 16,
                    }}
                >
                    {demos.map((d) => (
                        <DemoCard key={d.slug} demo={d} />
                    ))}
                </div>
            )}
        </div>
    );
}


function DemoCard({ demo }) {
    const miss = Number.isFinite(demo.miss_distance_m)
        ? `${fmtNum(demo.miss_distance_m, 1)} m miss`
        : "—";
    const dur = Number.isFinite(demo.duration_s)
        ? `${fmtNum(demo.duration_s, 1)} s engagement`
        : "—";
    const outcomeLabel = (() => {
        const oc = (demo.outcome || "").toLowerCase();
        if (oc === "success" || oc === "intercept") return "Intercept";
        return demo.outcome || "—";
    })();
    return (
        <Link
            to={`/demos/${encodeURIComponent(demo.slug)}`}
            data-testid={`public-demo-card-${demo.slug}`}
            aria-label={`Open ${demo.display_name || demo.slug} demonstration`}
            style={{
                textDecoration: "none",
                color: "inherit",
                background: "var(--ux-bg-soft, #11181f)",
                border: "1px solid var(--ux-border-1, #232c3a)",
                borderRadius: 10,
                padding: 16,
                display: "flex",
                flexDirection: "column",
                gap: 8,
                transition: "border-color 0.15s",
            }}
            onMouseEnter={(e) =>
                (e.currentTarget.style.borderColor = "var(--ux-accent, #3da9fc)")
            }
            onMouseLeave={(e) =>
                (e.currentTarget.style.borderColor = "var(--ux-border-1, #232c3a)")
            }
        >
            <div
                style={{
                    fontSize: 13,
                    color: "var(--ux-text-muted, #a7b1c2)",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                }}
            >
                {outcomeLabel}
            </div>
            <h2
                style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.3 }}
                data-testid={`public-demo-title-${demo.slug}`}
            >
                {demo.display_name || demo.slug}
            </h2>
            <p
                style={{
                    fontSize: 13,
                    color: "var(--ux-text-muted, #a7b1c2)",
                    flex: 1,
                    margin: 0,
                    lineHeight: 1.5,
                }}
            >
                {demo.description}
            </p>
            <div
                style={{
                    display: "flex",
                    gap: 12,
                    fontSize: 12,
                    color: "var(--ux-text-muted, #a7b1c2)",
                    paddingTop: 8,
                    borderTop: "1px solid var(--ux-border-1, #232c3a)",
                }}
            >
                <span data-testid={`public-demo-miss-${demo.slug}`}>{miss}</span>
                <span aria-hidden="true">·</span>
                <span data-testid={`public-demo-duration-${demo.slug}`}>{dur}</span>
                <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <Play size={14} aria-hidden="true" /> Open
                </span>
            </div>
        </Link>
    );
}


// --------------------------------------------------------------------
// PublicDemoViewerPage — single demo at /demos/:slug
// --------------------------------------------------------------------
export function PublicDemoViewerPage() {
    const { slug } = useParams();
    const [meta, setMeta] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        _fetchJson("/api/public/demos")
            .then((d) => {
                if (cancelled) return;
                const found = (d?.demos || []).find((x) => x.slug === slug);
                if (!found) {
                    setError(`Unknown demonstration: ${slug}`);
                    return;
                }
                setMeta(found);
            })
            .catch((e) => {
                if (cancelled) return;
                setError(e?.message || "Failed to load demo metadata.");
            });
        return () => {
            cancelled = true;
        };
    }, [slug]);

    // Public fetcher wired to /api/public/demos/{slug}/trajectory-data.
    // Resolves with the same payload shape as runsApi.trajectoryData so
    // Trajectory3D consumes it without modification.
    const publicFetcher = (s) =>
        _fetchJson(`/api/public/demos/${encodeURIComponent(s)}/trajectory-data`);

    return (
        <div
            data-testid="public-demo-viewer-page"
            style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 16px" }}
        >
            <Link
                to="/demos"
                className="btn-tertiary"
                data-testid="public-demo-viewer-back"
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 16,
                    color: "var(--ux-text-muted, #a7b1c2)",
                    textDecoration: "none",
                }}
            >
                <ArrowLeft size={14} aria-hidden="true" /> All demonstrations
            </Link>

            {error && (
                <EmptyState
                    title="Demonstration not found"
                    description={String(error)}
                    data-testid="public-demo-viewer-error"
                />
            )}

            {!error && meta && (
                <>
                    <header style={{ marginBottom: 16 }}>
                        <h1
                            style={{ fontSize: 24, fontWeight: 600, marginBottom: 4 }}
                            data-testid="public-demo-viewer-title"
                        >
                            {meta.display_name}
                        </h1>
                        <p
                            style={{
                                color: "var(--ux-text-muted, #a7b1c2)",
                                fontSize: 14,
                                margin: 0,
                                lineHeight: 1.5,
                            }}
                            data-testid="public-demo-viewer-description"
                        >
                            {meta.description}
                        </p>
                        <div
                            style={{
                                display: "flex",
                                gap: 12,
                                marginTop: 8,
                                fontSize: 12,
                                color: "var(--ux-text-muted, #a7b1c2)",
                            }}
                            data-testid="public-demo-viewer-summary"
                        >
                            <span>
                                miss = {fmtNum(meta.miss_distance_m, 1)} m
                            </span>
                            <span>·</span>
                            <span>intercept t = {fmtNum(meta.intercept_time_s, 1)} s</span>
                            <span>·</span>
                            <span>duration = {fmtNum(meta.duration_s, 1)} s</span>
                        </div>
                    </header>

                    <div
                        style={{
                            border: "1px solid var(--ux-border-1, #232c3a)",
                            borderRadius: 10,
                            padding: 12,
                            background: "var(--ux-bg-soft, #11181f)",
                        }}
                        data-testid="public-demo-viewer-canvas"
                    >
                        <Trajectory3D
                            runId={meta.slug}
                            status="succeeded"
                            hasTrajectoryArtifact={true}
                            fetcher={publicFetcher}
                        />
                    </div>
                </>
            )}
        </div>
    );
}


export default PublicDemosListPage;
